import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Grid2, IconButton, Theme, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import NumericTextField from "../../../../../views/components/common/pop-up/NumericTextField";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import {
  formatShipmentBoxDimensions,
  formatShipmentBoxListLabel,
  getFirstActiveShipmentBox,
  getShipmentBoxApiErrorMessage,
  getShipmentBoxAwb,
  isShipmentBoxLocked,
  parseShipmentBoxDimensions,
  ShipmentBox,
  sortShipmentBoxesByIdAsc,
  sortShipmentBoxesForDisplay,
} from "../../../../../models/Shipments/ShipmentBoxes";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import ShipmentBoxListMeta from "../../components/universal-shipment/ShipmentBoxListMeta";

type BoxFormFields = {
  length: string;
  breadth: string;
  height: string;
  weight: string;
  awb: string;
};

const EMPTY_BOX_FORM: BoxFormFields = {
  length: "",
  breadth: "",
  height: "",
  weight: "",
  awb: "",
};

type EditorState =
  | { type: "closed" }
  | { type: "add" }
  | { type: "edit"; box: ShipmentBox };

interface ShipmentBoxesSectionProps {
  shipmentId: number;
  boxes: ShipmentBox[] | null | undefined;
  canEdit: boolean;
  shipmentsAPI: ShipmentsAPI;
  onBoxesChange: (boxes: ShipmentBox[]) => void;
}

const toFormFields = (box: ShipmentBox): BoxFormFields => ({
  length: String(box.length ?? ""),
  breadth: String(box.breadth ?? ""),
  height: String(box.height ?? ""),
  weight: String(box.weight ?? ""),
  awb: getShipmentBoxAwb(box) || "",
});

const ShipmentBoxesSection: React.FC<ShipmentBoxesSectionProps> = ({
  shipmentId,
  boxes,
  canEdit,
  shipmentsAPI,
  onBoxesChange,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const sortedBoxes = useMemo(
    () => sortShipmentBoxesForDisplay(boxes),
    [boxes],
  );
  const firstBoxId = getFirstActiveShipmentBox(boxes)?.id ?? null;

  const [editor, setEditor] = useState<EditorState>({ type: "closed" });
  const [form, setForm] = useState<BoxFormFields>(EMPTY_BOX_FORM);
  const [pendingRemoveId, setPendingRemoveId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setEditor({ type: "closed" });
    setForm(EMPTY_BOX_FORM);
    setPendingRemoveId(null);
    setBusy(false);
    setError(null);
    setSuccess(null);
  }, [shipmentId]);

  const canReplaceBoxValues = (box: ShipmentBox) =>
    box.id !== firstBoxId || sortedBoxes.length === 1;

  const canRemoveBox = (box: ShipmentBox) => !isShipmentBoxLocked(box);

  const updateFormField = (field: keyof BoxFormFields, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openAddEditor = () => {
    setError(null);
    setSuccess(null);
    setPendingRemoveId(null);
    setForm(EMPTY_BOX_FORM);
    setEditor({ type: "add" });
  };

  const openEditEditor = (box: ShipmentBox) => {
    if (!canReplaceBoxValues(box)) {
      setError(
        "The first box is used for carrier booking and cannot be replaced while other boxes exist. Add a new box, then remove this one if you want to change booking dimensions or AWB.",
      );
      setSuccess(null);
      return;
    }
    setError(null);
    setSuccess(null);
    setPendingRemoveId(null);
    setForm(toFormFields(box));
    setEditor({ type: "edit", box });
  };

  const closeEditor = () => {
    setEditor({ type: "closed" });
    setForm(EMPTY_BOX_FORM);
  };

  const handleSaveBox = async () => {
    try {
      const payload = parseShipmentBoxDimensions(form);
      if (!payload) {
        setError(
          "Length, breadth, height, and weight must all be greater than 0.",
        );
        setSuccess(null);
        return;
      }

      if (editor.type === "edit" && !canReplaceBoxValues(editor.box)) {
        setError(
          "The first box is used for carrier booking and cannot be replaced while other boxes exist. Add a new box, then remove this one if you want to change booking dimensions or AWB.",
        );
        setSuccess(null);
        return;
      }

      setBusy(true);
      setError(null);
      setSuccess(null);

      if (editor.type === "add") {
        const result = await shipmentsAPI.addShipmentBox(shipmentId, payload);
        onBoxesChange(sortShipmentBoxesByIdAsc(result.boxes));
        setSuccess("Box added.");
        closeEditor();
        return;
      }

      if (editor.type === "edit") {
        const currentBox =
          sortedBoxes.find((box) => box.id === editor.box.id) ?? editor.box;
        if (!canReplaceBoxValues(currentBox)) {
          setError(
            "The first box is used for carrier booking and cannot be replaced while other boxes exist. Add a new box, then remove this one if you want to change booking dimensions or AWB.",
          );
          return;
        }

        const added = await shipmentsAPI.addShipmentBox(shipmentId, payload);
        try {
          const deleted = await shipmentsAPI.deleteShipmentBox(
            shipmentId,
            editor.box.id,
          );
          onBoxesChange(sortShipmentBoxesByIdAsc(deleted.boxes));
        } catch (deleteError) {
          onBoxesChange(sortShipmentBoxesByIdAsc(added.boxes));
          throw deleteError;
        }
        setSuccess("Box updated.");
        closeEditor();
      }
    } catch (err: unknown) {
      setError(getShipmentBoxApiErrorMessage(err, "Failed to save box."));
    } finally {
      setBusy(false);
    }
  };

  const requestRemoveBox = (box: ShipmentBox) => {
    if (isShipmentBoxLocked(box)) {
      setError(
        "This box is locked because an AWB is assigned and cannot be removed.",
      );
      setSuccess(null);
      setPendingRemoveId(null);
      return;
    }
    setError(null);
    setSuccess(null);
    setPendingRemoveId(box.id);
  };

  const handleConfirmRemove = async (boxId: number) => {
    const boxToRemove = sortedBoxes.find((box) => box.id === boxId);
    if (boxToRemove && isShipmentBoxLocked(boxToRemove)) {
      setError(
        "This box is locked because an AWB is assigned and cannot be removed.",
      );
      setSuccess(null);
      setPendingRemoveId(null);
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await shipmentsAPI.deleteShipmentBox(shipmentId, boxId);
      onBoxesChange(sortShipmentBoxesByIdAsc(result.boxes));
      setPendingRemoveId(null);
      if (editor.type === "edit" && editor.box.id === boxId) {
        closeEditor();
      }
      setSuccess("Box removed.");
    } catch (err: unknown) {
      setError(getShipmentBoxApiErrorMessage(err, "Failed to remove box."));
    } finally {
      setBusy(false);
    }
  };

  const pendingRemoveBox = sortedBoxes.find(
    (box) => box.id === pendingRemoveId,
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography variant="heading7" sx={{ color: colors.title }}>
          Boxes ({sortedBoxes.length})
        </CustomTypography>
        {canEdit && editor.type !== "add" && (
          <AppButton
            label="Add box"
            variant="secondary"
            onClick={openAddEditor}
            startIcon={<AddIcon />}
            disabled={busy}
            customStyles={{ minHeight: 32, px: 1.5 }}
            fontSize="0.75rem"
          />
        )}
      </Box>

      <CustomTypography
        variant="body3"
        sx={{ color: colors.subtitle, mb: 1.25, display: "block" }}
      >
        Each row is a shipment box. Booking uses the first box (lowest id).
      </CustomTypography>

      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 1.25 }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 1.25 }}
        >
          {error}
        </Alert>
      )}

      {sortedBoxes.length === 0 && editor.type === "closed" ? (
        <Box
          sx={{
            py: 3,
            px: 1.5,
            border: `1px dashed ${colors.border}`,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <CustomTypography variant="body2" sx={{ color: colors.subtitle }}>
            No boxes on this shipment.
          </CustomTypography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sortedBoxes.map((box) => {
            const isFirst = box.id === firstBoxId;
            const isLocked = isShipmentBoxLocked(box);
            const isPendingRemove = pendingRemoveId === box.id && !isLocked;
            const isEditing =
              editor.type === "edit" && editor.box.id === box.id;
            const showActions =
              canEdit && (canReplaceBoxValues(box) || canRemoveBox(box));

            return (
              <Box
                key={box.id}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${
                    isPendingRemove
                      ? theme.palette.warning.light
                      : isEditing
                        ? colors.accent
                        : colors.border
                  }`,
                  bgcolor: isPendingRemove
                    ? `${theme.palette.warning.main}0F`
                    : colors.surface,
                  overflow: "hidden",
                }}
              >
                <Grid2
                  container
                  spacing={1}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                  alignItems="flex-start"
                >
                  <Grid2
                    size={{
                      xs: 12,
                      sm: showActions ? 9 : 12,
                      md: showActions ? 10 : 12,
                    }}
                    sx={{ minWidth: 0 }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: colors.title,
                        wordBreak: "break-word",
                      }}
                    >
                      {formatShipmentBoxListLabel(box)}
                      {isFirst ? " · Booking box" : ""}
                    </CustomTypography>
                    <CustomTypography
                      variant="body3"
                      sx={{
                        color: colors.subtitle,
                        mt: 0.25,
                        display: "block",
                      }}
                    >
                      {formatShipmentBoxDimensions(box)}
                    </CustomTypography>
                    {isFirst && sortedBoxes.length > 1 && !isLocked && (
                      <CustomTypography
                        variant="caption"
                        sx={{
                          color: colors.subtitle,
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        Used for carrier booking. Values cannot be replaced
                        while other boxes exist — add a new box, then remove
                        this one if you need to change booking dimensions.
                      </CustomTypography>
                    )}
                    <ShipmentBoxListMeta box={box} />
                  </Grid2>
                  {showActions && (
                    <Grid2
                      size={{ xs: 12, sm: 3, md: 2 }}
                      sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {canReplaceBoxValues(box) && (
                          <IconButton
                            size="small"
                            aria-label={`Edit ${formatShipmentBoxListLabel(box)}`}
                            disabled={busy}
                            onClick={() => openEditEditor(box)}
                            sx={{ color: colors.accent }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canRemoveBox(box) && (
                          <IconButton
                            size="small"
                            aria-label={`Remove ${formatShipmentBoxListLabel(box)}`}
                            disabled={busy}
                            onClick={() => requestRemoveBox(box)}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Grid2>
                  )}
                </Grid2>
              </Box>
            );
          })}
        </Box>
      )}

      {pendingRemoveBox && canEdit && !isShipmentBoxLocked(pendingRemoveBox) && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.warning.light}`,
            bgcolor: `${theme.palette.warning.main}0F`,
          }}
        >
          <CustomTypography
            variant="body2"
            sx={{ color: colors.title, mb: 1.25, lineHeight: 1.45 }}
          >
            {pendingRemoveBox.id === firstBoxId && sortedBoxes.length > 1
              ? `Remove ${formatShipmentBoxListLabel(pendingRemoveBox)}? Booking will switch to the next remaining box (lowest id).`
              : `Remove ${formatShipmentBoxListLabel(pendingRemoveBox)} (${formatShipmentBoxDimensions(pendingRemoveBox)})?`}
          </CustomTypography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <AppButton
              label="Keep"
              variant="secondary"
              disabled={busy}
              onClick={() => setPendingRemoveId(null)}
              customStyles={{ minHeight: 32, px: 1.5 }}
              fontSize="0.75rem"
            />
            <AppButton
              label={busy ? "Removing…" : "Remove box"}
              variant="primary"
              disabled={busy}
              onClick={() => handleConfirmRemove(pendingRemoveBox.id)}
              customStyles={{
                minHeight: 32,
                px: 1.5,
                bgcolor: theme.palette.error.main,
                "&:hover": { bgcolor: theme.palette.error.dark },
              }}
              fontSize="0.75rem"
            />
          </Box>
        </Box>
      )}

      {canEdit && editor.type !== "closed" && (
        <Box
          sx={{
            mt: 1.5,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderBottom: `1px solid ${colors.border}`,
              bgcolor: colors.surfaceMuted,
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={600}
              sx={{ color: colors.title }}
            >
              {editor.type === "add"
                ? "Add box"
                : `Update ${formatShipmentBoxListLabel(editor.box)}`}
            </CustomTypography>
            <IconButton
              size="small"
              aria-label="Close box editor"
              onClick={closeEditor}
              disabled={busy}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 1.5 }}>
            {editor.type === "edit" && (
              <CustomTypography
                variant="body3"
                sx={{ color: colors.subtitle, mb: 1.25, display: "block" }}
              >
                There is no update-box API. Saving adds a new box with these
                dimensions and AWB, then removes this one.
              </CustomTypography>
            )}
            <Grid2 container spacing={1.5} columns={{ xs: 12, sm: 12, md: 12 }}>
              {(
                [
                  ["length", "Length (cm)"],
                  ["breadth", "Breadth (cm)"],
                  ["height", "Height (cm)"],
                  ["weight", "Weight (kg)"],
                ] as Array<
                  ["length" | "breadth" | "height" | "weight", string]
                >
              ).map(([field, label]) => (
                <Grid2 key={field} size={{ xs: 12, sm: 6, md: 3 }}>
                  <CustomTypography
                    variant="body3"
                    sx={{ mb: 0.5, fontWeight: 500, color: colors.title }}
                  >
                    {label}
                  </CustomTypography>
                  <NumericTextField
                    size="small"
                    fullWidth
                    value={form[field]}
                    disabled={busy}
                    onChange={(event) =>
                      updateFormField(field, event.target.value)
                    }
                    sx={{
                      "& .MuiInputBase-input": {
                        maxHeight: "36px",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </Grid2>
              ))}
              <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                <CustomTypography
                  variant="body3"
                  sx={{ mb: 0.5, fontWeight: 500, color: colors.title }}
                >
                  Box AWB
                </CustomTypography>
                <CustomTextField
                  placeholder="Optional AWB for this box"
                  value={form.awb}
                  disabled={busy}
                  width="100%"
                  onChange={(event) =>
                    updateFormField("awb", event.target.value)
                  }
                  customStyles={{ marginBottom: 0 }}
                />
              </Grid2>
            </Grid2>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1.5,
              }}
            >
              <AppButton
                label="Cancel"
                variant="secondary"
                disabled={busy}
                onClick={closeEditor}
                customStyles={{ minHeight: 32, px: 1.5 }}
                fontSize="0.75rem"
              />
              <AppButton
                label={
                  busy
                    ? "Saving…"
                    : editor.type === "add"
                      ? "Add box"
                      : "Save box"
                }
                variant="primary"
                disabled={busy}
                onClick={handleSaveBox}
                customStyles={{ minHeight: 32, px: 1.5 }}
                fontSize="0.75rem"
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ShipmentBoxesSection;
